import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronLeft, ChevronRight, Download, Info } from '@digital-twins/design-system/icons'
import { Button } from '@design-system/components/button'
import { Text } from '@design-system/components/Text'
import { Tabs, TabsList, TabsTrigger } from '@design-system/components/tabs'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { intlLocaleForUi } from '@/i18n'
import { cn } from '@/lib/utils'

import {
  formatSignedUsageCredits,
  formatUsageCredits,
  formatWeightedTokens,
  isRefundRow,
  nestUsageRow,
  rowHasDetail,
  rowExtras,
  rowWeightedTokens,
} from './dailyUsage'

import type { DailyUsageChild, DailyUsagePage, DailyUsageRow } from '@/lib/api'

export interface DailyUsageLogProps {
  data: DailyUsagePage | undefined
  loading?: boolean
  /** Shown for rows started by the viewer or by a user the API does not name. */
  viewerFallbackDisplay: string
  /** Display name per member token (team rows). */
  memberNames?: Record<string, string | null>
  page: number
  onPageChange: (page: number) => void
  onExport: () => void
  isExporting?: boolean
  /** Admins only: the log's scope. With `onScopeChange`, a Personal / Team toggle shows. */
  scope?: 'me' | 'team'
  onScopeChange?: (scope: 'me' | 'team') => void
}

/** A local day `YYYY-MM-DD` shown in the UI language, without a timezone shift. */
function formatUsageDay(day: string, locale: string): string {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) {
    return day
  }
  return new Date(y, m - 1, d).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** One extra of an expanded row: type · name, then its credits. */
function ChildRow(props: { child: DailyUsageChild }) {
  const { child } = props
  const { t } = useTranslation('app')
  const isRefunded = child.state === 'refunded' || child.state === 'partially_refunded' || child.state === 'refund'
  const parts = [t(`billing.usage.child.${child.type}`), child.title?.trim() || null].filter(
    (part): part is string => Boolean(part),
  )

  if (child.link) {
    // A run this reply started: its credits are on the run's own row.
    return (
      <li className="py-2 text-sm text-muted-foreground" data-testid="billing-usage-child-workflow-link">
        {[t('billing.usage.workflowLink'), child.title?.trim() || null].filter(Boolean).join(' · ')}
      </li>
    )
  }

  return (
    <li className="flex items-baseline justify-between gap-3 py-2" data-testid={`billing-usage-child-${child.type}`}>
      <span className="min-w-0 truncate text-sm text-foreground" data-testid="billing-usage-child-line">
        {parts.join(' · ')}
        {child.newVideo ? (
          <span className="ml-2 text-xs text-muted-foreground" data-testid="billing-usage-child-new-video">
            {t('billing.usage.newVideo', { multiplier: child.videoMultiplier ?? 2 })}
          </span>
        ) : null}
        {isRefunded ? (
          <span className="ml-2 text-xs text-muted-foreground">{t(`billing.usage.state.${child.state}`)}</span>
        ) : null}
      </span>
      <span className="shrink-0 tabular-nums text-sm text-foreground" data-testid="billing-usage-child-credits">
        {formatSignedUsageCredits(child.accruedMillicredits)}
      </span>
    </li>
  )
}

function UsageRow(props: { row: DailyUsageRow; userLabel: string; showMember: boolean; locale: string; isLast: boolean }) {
  const { row: rawRow, userLabel, showMember, locale, isLast } = props
  const { t } = useTranslation('app')
  const [isOpen, setIsOpen] = useState(false)
  const row = nestUsageRow(rawRow)
  const hasDetail = rowHasDetail(row)
  const isRefund = isRefundRow(row)
  const weighted = rowWeightedTokens(row)
  const key = `${row.date}-${row.publicId ?? row.title ?? row.kind}`
  const border = isLast && !isOpen ? '' : 'border-b border-border/40 dark:border-border'

  return (
    <Fragment>
      <tr data-testid="billing-usage-day-row" data-row-key={key}>
        <td className={cn('whitespace-nowrap px-3 py-3.5 text-muted-foreground', border)}>
          {/* Only rows with something more to show expand; the others keep the chevron's space. */}
          {hasDetail ? (
            <button
              type="button"
              onClick={() => setIsOpen((open) => !open)}
              aria-expanded={isOpen}
              aria-label={t('billing.usage.toggleRow')}
              data-testid="billing-usage-day-toggle"
              className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted"
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen ? '' : '-rotate-90')} aria-hidden />
            </button>
          ) : (
            <span className="mr-2 inline-block h-5 w-5 align-middle" aria-hidden />
          )}
          {formatUsageDay(row.date, locale)}
        </td>
        {showMember ? (
          <td className={cn('px-3 py-3.5 font-medium text-foreground', border)}>{userLabel}</td>
        ) : null}
        <td className={cn('px-3 py-3.5', border)}>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {isRefund ? t('billing.usage.state.refund') : t(`billing.usage.kind.${row.kind}`)}
          </span>
        </td>
        <td className={cn('px-3 py-3.5 text-foreground', border)}>
          {/* A long title shortens; the token total stays whole. */}
          <div className="flex max-w-[360px] items-baseline">
            <span className="min-w-0 truncate">
              {row.title ??
                (row.kind === 'study'
                  ? row.deleted
                    ? row.publicId
                      ? t('billing.usage.deletedStudyId', { id: row.publicId.slice(0, 8) })
                      : t('billing.usage.deletedStudy')
                    : t('billing.usage.untitledStudy')
                  : t('billing.usage.untitled'))}
            </span>
            {weighted !== null ? (
              <span className="shrink-0 whitespace-pre text-muted-foreground" data-testid="billing-usage-day-weighted">
                {' · '}
                {t('billing.usage.weightedTokens', { value: formatWeightedTokens(weighted, locale) })}
              </span>
            ) : null}
          </div>
        </td>
        <td
          className={cn('px-3 py-3.5 text-right tabular-nums text-foreground', border)}
          data-testid="billing-usage-day-credits"
        >
          {formatSignedUsageCredits(row.accruedMillicredits)}
        </td>
      </tr>
      {isOpen && hasDetail ? (
        <tr data-testid="billing-usage-day-children">
          <td colSpan={showMember ? 5 : 4} className={cn('bg-muted/20 px-3 pb-3 pt-1', isLast ? '' : 'border-b border-border/40 dark:border-border')}>
            <ul className="divide-y divide-border/40">
              {rowExtras(row).map((child) => (
                <ChildRow key={child.token} child={child} />
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </Fragment>
  )
}

/**
 * The enrolled usage log (credit metering, Step 10): one expandable row per conversation per
 * day from the daily usage API, Assistant usage first with images, Quick Studies, studies and
 * room messages nested under it. Usage is the accrued amount (`<0.01` for a tiny positive);
 * whole-credit debits are an internal posting detail and never shown here, and nothing set
 * aside is ever shown as spent.
 */
export function DailyUsageLog(props: DailyUsageLogProps) {
  const { data, loading, viewerFallbackDisplay, memberNames, page, onPageChange, onExport, isExporting, scope = 'me', onScopeChange } = props
  const { t, i18n } = useTranslation(['app', 'common'])
  const locale = intlLocaleForUi(i18n.language)
  const rows = data?.rows ?? []
  const totalPages = Math.max(1, data?.totalPages ?? 1)
  // With the toggle, the Member column belongs to Team; without it (members) it stays as it was.
  const showMemberColumn = !onScopeChange || scope === 'team'
  const columnCount = showMemberColumn ? 5 : 4
  const waivedMillicredits = (data?.months ?? []).reduce((sum, month) => sum + month.waivedMillicredits, 0)

  function userLabelOf(row: DailyUsageRow): string {
    if (!row.userToken) {
      return viewerFallbackDisplay
    }
    return memberNames?.[row.userToken] ?? viewerFallbackDisplay
  }

  return (
    <div className="flex flex-col gap-4" data-testid="billing-usage-log">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex items-center gap-3">
          <Text size="sm" weight="medium" className="whitespace-nowrap">
            {t('billing.usageLog')}
          </Text>
          {onScopeChange ? (
            <Tabs value={scope} onValueChange={(next) => onScopeChange(next as 'me' | 'team')}>
              <TabsList className="h-8 rounded-lg bg-muted p-0.5" data-testid="billing-usage-scope">
                <TabsTrigger value="me" variant="segmented" className="h-7" data-testid="billing-usage-scope-me">
                  {t('billing.usageScope.personal')}
                </TabsTrigger>
                <TabsTrigger value="team" variant="segmented" className="h-7" data-testid="billing-usage-scope-team">
                  {t('billing.usageScope.team')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onExport}
          disabled={isExporting || !data}
          data-testid="billing-usage-export"
          className="ml-auto h-9 shrink-0 gap-1.5 whitespace-nowrap rounded-md border-cta px-3 text-sm font-medium text-cta hover:bg-cta/5 dark:border-cta dark:text-cta dark:hover:bg-cta/10"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          {t('billing.exportCsv')}
        </Button>
      </div>

      <div className="-mx-4 overflow-x-auto md:mx-0">
        <table className="w-full min-w-[540px] border-separate border-spacing-0 text-left text-sm">
          <thead>
            <tr>
              <th scope="col" className="rounded-l-xl bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">{t('billing.colDate')}</th>
              {showMemberColumn ? (
                <th scope="col" className="bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">{t('billing.colUser')}</th>
              ) : null}
              <th scope="col" className="bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">{t('billing.colType')}</th>
              <th scope="col" className="bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">{t('billing.colName')}</th>
              <th scope="col" className="rounded-r-xl bg-muted/50 px-3 py-3 text-right text-xs font-normal text-muted-foreground">
                <span className="inline-flex items-center justify-end gap-1">
                  {t('billing.colCredits')}
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('billing.usage.weightedInfoLabel')}
                          data-testid="billing-usage-weighted-info"
                          className="rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                        >
                          <Info className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs">
                        {t('billing.usage.weightedInfo')}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </th>
            </tr>
          </thead>
          <tbody data-testid="billing-usage-log-tbody">
            {loading && !data ? (
              <tr>
                <td colSpan={columnCount} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {t('loading', { ns: 'common' })}
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  {t('billing.noUsage')}
                </td>
              </tr>
            ) : null}
            {rows.map((row, index) => (
              <UsageRow
                key={`${row.date}-${row.userToken ?? 'me'}-${row.publicId ?? row.title ?? index}-${index}`}
                row={row}
                userLabel={userLabelOf(row)}
                showMember={showMemberColumn}
                locale={locale}
                isLast={index === rows.length - 1}
              />
            ))}
          </tbody>
        </table>
      </div>

      {data ? (
        <div className="flex flex-col items-center justify-between gap-2 sm:flex-row" data-testid="billing-usage-summary">
          <Text size="xs" color="muted">
            {t('billing.usage.totals', { used: formatUsageCredits(data.totals.accruedMillicredits) })}
            {waivedMillicredits > 0
              ? ` · ${t('billing.usage.waived', { value: formatUsageCredits(waivedMillicredits) })}`
              : ''}
          </Text>
          {totalPages > 1 ? (
            <div className="flex items-center gap-1" data-testid="billing-usage-pagination">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1 || loading}
                aria-label={t('billing.prevPage')}
                data-testid="billing-usage-page-prev"
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </Button>
              <span className="px-2 text-xs tabular-nums text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || loading}
                aria-label={t('billing.nextPage')}
                data-testid="billing-usage-page-next"
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
